import { latencyCalibrationLexiconId, type MelodyTrainerMode, type VocalLib } from '@tunasong/music-lib'
import type { Melody } from '@tunasong/schemas'
import { Button, Checkbox, FormControlLabel, Radio, RadioGroup } from '@tunasong/ui-lib'
import { useState } from 'react'
import type { FC } from 'react'
import { MelodyMiniRoll } from '../melody-roll/melody-mini-roll.js'
import { useVocalCopy } from '../pitch-training/use-vocal-copy.js'
import { TheoryGlyphs } from '../theory-copy/theory-glyphs.js'
import styles from './melody-trainer-welcome.module.css'

/**
 * The Melody Trainer welcome screen: pick how you are going to play it, clear the prerequisites,
 * start.
 *
 * The prerequisites are GATES, not warnings, and that is the whole reason this screen exists.
 * Without a calibrated input latency the timing score absorbs the device's delay and reports an
 * even player as dragging (§6.3); without a calibrated vocal range a bass is handed a soprano
 * melody and a failure of tessitura is recorded as a failure of ear (vocal §5.8); without
 * headphones the click is captured on every beat and segmented as notes (§5). Each of the three is
 * something the tool cannot work around, so `Start` stays disabled and the matching locked
 * sentence says which one is missing.
 *
 * The one sentence here that is NOT a lexicon verdict is the transposition notice. §6 requires the
 * transposition be stated, and it describes what the *tool* did rather than what the *user* did -
 * so it is UI copy, like the mode labels beside it.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §6, §6.3, §10
 */

const MODE_LABEL: Record<MelodyTrainerMode, string> = {
  voice: 'Voice — sing it',
  instrument: 'Instrument — guitar or piano, through the microphone',
  midi: 'MIDI — play it on a connected keyboard',
}

/** Which modes open a microphone, and therefore need headphones and a vocal-range check. */
const isAudioMode = (mode: MelodyTrainerMode) => mode !== 'midi'

/** Whole octaves only, so this never has to name an interval. */
const transpositionNotice = (semitones: number): string | null => {
  if (semitones === 0) {
    return null
  }
  const octaves = Math.abs(semitones) / 12
  const direction = semitones < 0 ? 'down' : 'up'
  const count = octaves === 1 ? 'an octave' : `${octaves} octaves`
  return `Transposed ${direction} ${count} to fit your range.`
}

export interface MelodyTrainerWelcomeProps {
  melody: Melody
  /** Pre-selected mode from `?mode=`, or null for no pre-selection. */
  preSelected: MelodyTrainerMode | null
  /** The stored vocal range, or null when the singer has not calibrated. */
  range: VocalLib.VocalRange | null
  /** The measured latency for the take device, or null when it has not been measured. */
  inputLatencyMs: number | null
  deviceLabel: string | null
  headphonesConfirmed: boolean
  midiAvailable: boolean
  /** Semitones voice mode would move the melody by. 0 when it fits as it stands. */
  transposedBy: number
  onConfirmHeadphones: (confirmed: boolean) => void
  onCalibrateLatency: (mode: MelodyTrainerMode) => void
  onStart: (mode: MelodyTrainerMode) => void
}

export const MelodyTrainerWelcome: FC<MelodyTrainerWelcomeProps> = props => {
  const {
    melody,
    preSelected,
    range,
    inputLatencyMs,
    deviceLabel,
    headphonesConfirmed,
    midiAvailable,
    transposedBy,
    onConfirmHeadphones,
    onCalibrateLatency,
    onStart,
  } = props

  const [mode, setMode] = useState<MelodyTrainerMode>(preSelected ?? 'voice')

  const intro = useVocalCopy('timing-intro')
  const latency = useVocalCopy(latencyCalibrationLexiconId(inputLatencyMs), {
    ms: inputLatencyMs === null ? undefined : Math.round(inputLatencyMs),
  })
  const rangeFirst = useVocalCopy('vocal-calibrate-first')
  const headphones = useVocalCopy('vocal-headphones')
  const privacy = useVocalCopy('vocal-privacy')

  const audio = isAudioMode(mode)
  const needsRange = mode === 'voice' && range === null
  const needsLatency = inputLatencyMs === null
  const needsHeadphones = audio && !headphonesConfirmed
  const notice = mode === 'voice' ? transpositionNotice(transposedBy) : null

  const canStart = melody.notes.length > 0 && !needsRange && !needsLatency && !needsHeadphones

  return (
    <section className={styles.welcome} aria-label="Melody Trainer welcome">
      <h2 className={styles.title}>Practice this melody</h2>

      {intro ? (
        <p className={styles.intro}>
          <TheoryGlyphs>{intro.body}</TheoryGlyphs>
        </p>
      ) : null}

      <MelodyMiniRoll melody={melody} className={styles.roll} />

      <RadioGroup
        aria-label="How you will play it"
        name="melody-trainer-mode"
        value={mode}
        onChange={(_event, value) => setMode(value as MelodyTrainerMode)}
        className={styles.modes}
      >
        <FormControlLabel value="voice" control={<Radio />} label={MODE_LABEL.voice} />
        <FormControlLabel value="instrument" control={<Radio />} label={MODE_LABEL.instrument} />
        <FormControlLabel
          value="midi"
          /* No input, no mode: a MIDI attempt with nothing connected would record silence. */
          control={<Radio disabled={!midiAvailable} />}
          label={MODE_LABEL.midi}
        />
      </RadioGroup>

      {notice ? <p className={styles.notice}>{notice}</p> : null}

      {melody.notes.length === 0 ? (
        <p className={styles.problem}>This melody has no notes yet — add some before practising it.</p>
      ) : null}

      {needsRange && rangeFirst ? (
        <p className={styles.problem}>
          <TheoryGlyphs>{rangeFirst.body}</TheoryGlyphs>
        </p>
      ) : null}

      {latency ? (
        <p className={needsLatency ? styles.problem : styles.ready}>
          <TheoryGlyphs>{latency.body}</TheoryGlyphs>
        </p>
      ) : null}

      {needsLatency ? (
        <Button variant="outlined" onClick={() => onCalibrateLatency(mode)}>
          Measure input delay
        </Button>
      ) : null}

      {audio ? (
        <div className={styles.headphones}>
          <FormControlLabel
            label="I am wearing headphones"
            control={<Checkbox checked={headphonesConfirmed} onCheckedChange={onConfirmHeadphones} />}
          />
          {headphones ? (
            <span className={styles.headphonesWhy}>
              <TheoryGlyphs>{headphones.body}</TheoryGlyphs>
            </span>
          ) : null}
        </div>
      ) : null}

      {audio && privacy ? (
        <p className={styles.privacy}>
          <TheoryGlyphs>{privacy.body}</TheoryGlyphs>
        </p>
      ) : null}

      <div className={styles.actions}>
        <Button variant="contained" color="primary" size="large" disabled={!canStart} onClick={() => onStart(mode)}>
          Start
        </Button>
        {deviceLabel ? <span className={styles.device}>{deviceLabel}</span> : null}
      </div>
    </section>
  )
}
